import { createFileRoute, notFound } from "@tanstack/react-router"

import ogImage from "@/core/assets/og.png"
import { UI } from "@/core/i18n/ui"
import { articleJsonLd, breadcrumbJsonLd, buildHead } from "@/core/seo"

import { GuidePage } from "../components/landing"
import type { Guide } from "../content/guide-types"
import { getGuide } from "../content/guides-registry"

/** Article + BreadcrumbList only: no FAQPage (seo-spec) and no Offer. */
export const guideSeo = (
  guide: Guide,
  path = `/guides/${guide.slug}`,
  /** Chinese pages (T1320): "zh" prefixes the canonical and names the breadcrumb in Chinese. */
  localePrefix?: "zh",
) => {
  const zh = localePrefix === "zh"
  return buildHead({
    title: guide.title,
    description: guide.description,
    path,
    ...(zh ? { localePrefix } : {}),
    type: "article",
    jsonLd: [
      articleJsonLd({
        headline: guide.h1,
        path,
        ...(zh ? { localePrefix } : {}),
        // No per-guide image yet; the real site-wide Open Graph image, not an invented one.
        image: ogImage,
        datePublished: guide.published,
        dateModified: guide.reviewed,
      }),
      breadcrumbJsonLd(
        zh
          ? [
              { name: UI.zh.home, path: "/zh" },
              { name: UI.zh.guides, path: "/guides" },
              { name: guide.crumb },
            ]
          : [
              { name: "Home", path: "/" },
              { name: "Guides", path: "/guides" },
              { name: guide.crumb },
            ],
      ),
    ],
  })
}

export const Route = createFileRoute("/_brand-layout/guides/$slug")({
  loader: ({ params }) => {
    if (!getGuide(params.slug)) throw notFound()
  },
  head: ({ params }) => {
    const guide = getGuide(params.slug)
    if (!guide) return { meta: [] }
    const { meta, links, scripts } = guideSeo(guide)
    return { meta, links, scripts }
  },
  component: GuideRoute,
})

function GuideRoute() {
  const { slug } = Route.useParams()
  const guide = getGuide(slug)
  if (!guide) throw notFound()
  return <GuidePage guide={guide} />
}
